'use client'

import { useState, useMemo, useCallback } from 'react'
import { ArrowUpDown, ArrowUp, ArrowDown, Check, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { TablePayload, TableColumn, HighlightRule } from '../svs-types'
import { resolveColor } from '../color-mapping'

interface TableRendererProps {
  payload: TablePayload
}

function formatCell(value: unknown, column: TableColumn): string {
  if (value == null) return '—'
  switch (column.type) {
    case 'number':
      return typeof value === 'number' ? value.toLocaleString() : String(value)
    case 'percent':
      return typeof value === 'number' ? `${value}%` : String(value)
    case 'currency':
      return typeof value === 'number'
        ? value.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 })
        : String(value)
    case 'date':
      return typeof value === 'string' ? new Date(value).toLocaleDateString() : String(value)
    default:
      return String(value)
  }
}

function CellBoolean({ value }: { value: unknown }) {
  return value ? (
    <Check className="size-4 text-green-600 dark:text-green-400" />
  ) : (
    <X className="size-4 text-red-600 dark:text-red-400" />
  )
}

function evaluateRule(rule: HighlightRule, cellValue: unknown): boolean {
  const v = typeof cellValue === 'number' ? cellValue : Number(cellValue)
  const target = typeof rule.value === 'number' ? rule.value : Number(rule.value)
  if (isNaN(v) || isNaN(target)) return false
  switch (rule.operator) {
    case 'gt': return v > target
    case 'gte': return v >= target
    case 'lt': return v < target
    case 'lte': return v <= target
    case 'eq': return v === target
    case 'neq': return v !== target
    default: return false
  }
}

function getAlignClass(align?: string, type?: string): string {
  if (align === 'center') return 'text-center'
  if (align === 'right') return 'text-right'
  if (align === 'left') return 'text-left'
  // Default: numbers right-aligned, everything else left
  if (type === 'number' || type === 'percent' || type === 'currency') return 'text-right'
  return 'text-left'
}

export function TableRenderer({ payload }: TableRendererProps) {
  const { columns, rows } = payload.data
  const options = payload.options || {}
  const [sortKey, setSortKey] = useState<string | null>(null)
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const [page, setPage] = useState(0)

  const handleSort = useCallback((key: string) => {
    if (!options.sortable) return
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
    setPage(0)
  }, [sortKey, options.sortable])

  const sortedRows = useMemo(() => {
    if (!sortKey) return rows
    return [...rows].sort((a, b) => {
      const av = a[sortKey]
      const bv = b[sortKey]
      if (av == null && bv == null) return 0
      if (av == null) return 1
      if (bv == null) return -1
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av
      }
      return sortDir === 'asc'
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av))
    })
  }, [rows, sortKey, sortDir])

  const pageSize = options.pageSize || 0
  const pagedRows = pageSize > 0
    ? sortedRows.slice(page * pageSize, (page + 1) * pageSize)
    : sortedRows
  const totalPages = pageSize > 0 ? Math.ceil(sortedRows.length / pageSize) : 1

  return (
    <div className="space-y-2">
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="min-w-full divide-y divide-border text-sm">
          <thead className="bg-muted/50">
            <tr>
              {columns.map(col => (
                <th
                  key={col.key}
                  className={cn(
                    'px-4 text-xs font-medium uppercase tracking-wider text-muted-foreground',
                    options.compact ? 'py-1.5' : 'py-2.5',
                    getAlignClass(col.align, col.type),
                    options.sortable && 'cursor-pointer select-none hover:text-foreground',
                  )}
                  onClick={() => handleSort(col.key)}
                >
                  <span className="inline-flex items-center gap-1">
                    {col.label}
                    {options.sortable && sortKey === col.key && (
                      sortDir === 'asc' ? <ArrowUp className="size-3" /> : <ArrowDown className="size-3" />
                    )}
                    {options.sortable && sortKey !== col.key && (
                      <ArrowUpDown className="size-3 opacity-30" />
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {pagedRows.map((row, ri) => (
              <tr key={ri} className="transition-colors hover:bg-muted/30">
                {columns.map(col => {
                  const cellValue = row[col.key]
                  const matchedRule = options.highlightRules?.find(r => r.column === col.key && evaluateRule(r, cellValue))
                  const highlightColor = matchedRule ? resolveColor(matchedRule.color) : undefined

                  return (
                    <td
                      key={col.key}
                      className={cn(
                        'px-4',
                        options.compact ? 'py-1.5' : 'py-2.5',
                        getAlignClass(col.align, col.type),
                      )}
                      style={highlightColor ? { color: highlightColor } : undefined}
                    >
                      {col.type === 'boolean' ? (
                        <CellBoolean value={cellValue} />
                      ) : (
                        <span className={matchedRule ? 'font-medium' : undefined}>
                          {formatCell(cellValue, col)}
                        </span>
                      )}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            Page {page + 1} of {totalPages} ({sortedRows.length} rows)
          </span>
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="rounded border border-border px-2 py-1 hover:bg-muted disabled:opacity-50"
            >
              Prev
            </button>
            <button
              type="button"
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="rounded border border-border px-2 py-1 hover:bg-muted disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
